import "es6-shim";
import "../../styles/ManualInput.css";
import * as React from 'react';
import { Component } from 'react';
import Validator from "../../services/Validator/HealthCard/Validator";
import { CountryCodes } from "../../constants/countries";
import { LabelledMaskedInput } from "../customFormComponents/LabelledMaskedInput";
import { LabelledInput } from "../customFormComponents/LabelledInput";
import { IGenericsLang, IGeneralCountryMappedLang, AllRegionSelectionOptions, ILabelSelect } from "../../services/Language/Types";
import { LabelledSelect } from "../customFormComponents/LabelledSelect";
import { HealthCardNumberMaskedFormatter } from "./HealthCardNumberMaskedFormatter";

export interface IManualInputProps {
    invalidNumberDetected: boolean,
    text: IGenericsLang,
    countryMappedStrings: IGeneralCountryMappedLang
    countryList: ILabelSelect[],
    stateList: AllRegionSelectionOptions,
    focused: boolean,
    defaultCountry: string,
    defaultRegion: string,
    keyboardValue: string,
    changeMode?: () => void,
    showKeyboard(): void,
    onCountryOrRegionChange(country: string, region: string): void;
    onSelectClick(): void,
    onEraseHealthCardNumber(): void,
    submitHealthCardNumber(val: string, country?: string, region?: string): void,
}

interface IManualInputState {
    healthCardNumber: string,
    country: string,
    state: string,
    mask: any,
    placeHolder: string,
    isValueValid: boolean
}
export class ManualInput extends Component<IManualInputProps, IManualInputState> {
    public readonly MAXIMUM_NUMBER_ATTEMPTS = 2;

    constructor(props: IManualInputProps) {
        super(props);
        this.state = {
            healthCardNumber: "",
            country: this.props.defaultCountry || CountryCodes.CA,
            state: this.props.defaultRegion || "QC",
            mask: Validator.getCountryAndRegionMask(this.props.defaultCountry || CountryCodes.CA, this.props.defaultRegion || "QC"),
            placeHolder: Validator.getCountryAndRegionPlaceHolder(this.props.defaultCountry || CountryCodes.CA, this.props.defaultRegion || "QC"),
            isValueValid: true
        };
        this.updateMask = this.updateMask.bind(this);
        this.getDisabledValue = this.getDisabledValue.bind(this);
    }

    public updateMask(country: string, state: string) {
        this.setState({
            mask: Validator.getCountryAndRegionMask(country, state),
            placeHolder: Validator.getCountryAndRegionPlaceHolder(country, state)
        }, () => {
            this.props.onCountryOrRegionChange(this.state.country, this.state.state);
        })
    }
    /**
     * @name getDisabledValue
     * @description Will disable the submit button if the health card number is invalid (number format).
     * @returns {boolean}
     * @memberof ManualInput
     */
    public getDisabledValue(): boolean {
        if (this.state.healthCardNumber.replace(/\s|\(|\)|_|-|\+/g, '').length === 0) {
            return true;
        }
        return !Validator.validateHealthCardNumber(this.state.healthCardNumber.toUpperCase().replace(/\s|\(|\)|_|-|\+/g, ''), this.state.country, this.state.state);
    }
    public componentWillReceiveProps(nextProps: IManualInputProps) {
        if (nextProps.keyboardValue !== this.state.healthCardNumber) {
            this.setState({ healthCardNumber: nextProps.keyboardValue, isValueValid: Validator.validateHealthCardNumber(this.state.healthCardNumber.toUpperCase().replace(/\s|\(|\)|_|-|\+/g, ''), this.state.country, this.state.state) });
        }
    }
    /**
     * @name renderMaskInput
     * @description Render a masked input if it corresponds to the appropriate country and state.
     * @returns {JSX.Element}
     * @memberof ManualInput
     */
    public renderMaskInput(): JSX.Element {
        let inputClassName = this.props.invalidNumberDetected ? "form-control inputmask-large-invalid" : "form-control inputmask-large"
        if (this.props.focused) {
            inputClassName = inputClassName + " focused";
        }
        if (this.state.mask !== null) {
            const isMaskedHealthCardNumberValid = Validator.validateHealthCardNumber(this.state.healthCardNumber.toUpperCase().replace(/\s|\(|\)|_|-|\+/g, ''), this.state.country, this.state.state);
            return (
                <LabelledMaskedInput
                    labelText={this.props.countryMappedStrings.healthCardIdLabel[this.state.country]}
                    mask={this.state.mask}
                    placeHolder={this.state.mask}
                    value={this.state.healthCardNumber}
                    onFocus={() => { this.props.showKeyboard() }}
                    onChange={(value) => { this.setState({ healthCardNumber: value }) }}
                    erasable={true}
                    isValid={isMaskedHealthCardNumberValid}
                    focused={this.props.focused}
                    validationMessage={this.props.text.messagesInstructions.InvalidMessage}
                    onErase={() => { this.setState({ healthCardNumber: "" }, this.props.onEraseHealthCardNumber) }}
                />
            );
        } else {
            return (
                <LabelledInput
                    focused={this.props.focused}
                    labelText={this.props.countryMappedStrings.healthCardIdLabel[this.state.country]}
                    value={this.state.healthCardNumber}
                    onFocus={() => { this.setState({ healthCardNumber: "" }, () => { this.props.showKeyboard() }) }}
                    onChange={(value) => { this.setState({ healthCardNumber: value }) }}
                    isValid={Validator.validateHealthCardNumber(this.state.healthCardNumber.toUpperCase().replace(/\s|\(|\)|_|-|\+/g, ''), this.state.country, this.state.state)}
                    validationMessage={this.props.text.messagesInstructions.InvalidMessage}
                    erasable={true}
                    onErase={() => { this.setState({ healthCardNumber: "" }, this.props.onEraseHealthCardNumber) }}
                />
            );
        }
    }
    /**
     * @name render
     * @description Main class render method.
     * @returns {JSX.Element}
     * @memberof ManualInput
     */
    public render(): JSX.Element {
        const textString = this.props.text;
        return (
            <div>
                <div className="row">
                    <div className="col-sm-8 col-sm-offset-2">
                        <br /><br />
                        {this.props.countryList.length > 1 &&
                            <LabelledSelect
                                labelText={this.props.text.labels.CountryLabel}
                                onChange={
                                    (value: string) => {
                                        this.setState({
                                            country: value,
                                            healthCardNumber: "",
                                            state: value === this.props.defaultCountry
                                                ? this.props.defaultRegion
                                                : this.props.stateList[value][0].value as string
                                        },
                                            () => {
                                                this.updateMask(this.state.country, this.state.state);
                                            })
                                    }}
                                id="countrySelect"
                                value={this.state.country}
                                options={this.props.countryList}
                                defaultValue={this.props.defaultCountry}
                                onClick={this.props.onSelectClick}
                            />
                        }
                        {this.props.stateList && this.props.stateList[this.state.country] && this.props.stateList[this.state.country].length > 1 &&
                            <LabelledSelect
                                labelText={this.props.countryMappedStrings.regionLabel[this.state.country]}
                                onChange={(value: string) => { this.setState({ state: value, healthCardNumber: "" }, () => { this.updateMask(this.state.country, this.state.state); }) }}
                                value={this.state.state}
                                options={this.props.stateList[this.state.country]}
                                id="regionSelect"
                                defaultValue={this.props.defaultRegion}
                                onClick={this.props.onSelectClick}
                            />
                        }
                        {this.renderMaskInput()}
                        {this.props.children}
                    </div>
                </div>

                <div className="row">
                    <div className="col-sm-12 text-center">
                        <button
                            id="submitButton"
                            className="buttons-blue"
                            disabled={this.getDisabledValue()}
                            onClick={() => this.props.submitHealthCardNumber(HealthCardNumberMaskedFormatter.format(this.state.healthCardNumber, this.state.country, this.state.state), this.state.country, this.state.state)}
                        >
                            {textString.labels.SubmitButton}
                        </button>
                    </div>
                </div>
                {this.props.changeMode &&
                    <div className="row">
                        <div className="col-sm-6 col-sm-offset-3 text-center">
                            <label
                                className="switch-link"
                                onClick={() => {
                                    this.setState({ healthCardNumber: "" },
                                        () => {
                                            this.props.onEraseHealthCardNumber();
                                            this.props.changeMode();
                                        }
                                    )
                                }}
                            >
                                <u>{textString.labels.ToScannerInputModeButton}</u>
                            </label>
                        </div>
                    </div>
                }
            </div>
        );
    }

}



// WEBPACK FOOTER //
// ./src/components/AcquireHealthCardNumber/ManualInput.tsx